// =====================================================
// BED — Modèle du wizard multi-étapes (logique PURE, testable)
// =====================================================
// Ce module ne connaît ni React, ni le réseau : il transforme les champs du
// formulaire BED + les valeurs saisies en tout ce dont l'UI a besoin (étapes,
// visibilité conditionnelle, exigences, état de la frise, liste des éléments
// manquants). Le serveur (`bed_compute_missing_items`, 00231) reste la SOURCE
// DE VÉRITÉ des éléments manquants persistés ; ce miroir client sert à donner
// un retour immédiat entre deux enregistrements, avec exactement les mêmes
// règles.

import type { BedDeferrableGroup, FormFieldWithValues, TargetRole } from '../../types/form';

// -----------------------------------------------------------------------
// ÉTAPES
// -----------------------------------------------------------------------

/** Identifiant d'étape = préfixe de `form_fields.order_index` (étape * 1000). */
export type BedStepId = 1 | 2 | 3 | 4 | 5 | 6;

export interface BedStep {
  id: BedStepId;
  /** Numéro affiché dans la frise. */
  index: number;
  title: string;
  /** Sous-titre court (mobile-first : on reste très bref). */
  hint: string;
}

export const BED_STEPS: readonly BedStep[] = [
  { id: 1, index: 1, title: 'Équipage', hint: 'Nom et numéro' },
  { id: 2, index: 2, title: 'Pilote', hint: 'Identité et documents' },
  { id: 3, index: 3, title: 'Copilote', hint: 'Identité et documents' },
  { id: 4, index: 4, title: 'Véhicule', hint: 'Immatriculation' },
  { id: 5, index: 5, title: 'Options', hint: 'Assurances et consentements' },
  { id: 6, index: 6, title: 'Récapitulatif', hint: 'Signature et paiement' },
] as const;

export const BED_LAST_STEP: BedStepId = 6;

/**
 * Étape d'un champ, dérivée de son `order_index` (convention 00231 :
 * `order_index = étape * 1000 + slot`). Un champ hors plage retombe sur
 * l'étape ① : il reste saisissable, jamais perdu.
 */
export function stepOfField(field: Pick<FormFieldWithValues, 'order_index'>): BedStepId {
  const step = Math.floor(field.order_index / 1000);
  return step >= 1 && step <= BED_LAST_STEP ? (step as BedStepId) : 1;
}

/** Regroupe les champs par étape, en conservant l'ordre `order_index`. */
export function groupFieldsByStep(
  fields: FormFieldWithValues[]
): Record<BedStepId, FormFieldWithValues[]> {
  const out = { 1: [], 2: [], 3: [], 4: [], 5: [], 6: [] } as Record<
    BedStepId,
    FormFieldWithValues[]
  >;
  [...fields]
    .sort((a, b) => a.order_index - b.order_index)
    .forEach((field) => out[stepOfField(field)].push(field));
  return out;
}

// -----------------------------------------------------------------------
// GROUPES REPORTABLES (« je m'engage à communiquer avant le … »)
// -----------------------------------------------------------------------

/**
 * Clés du dictionnaire de reports du brouillon : `<groupe>:<rôle>`.
 * `certificat` s'ajoute aux groupes SQL : c'est un upload, pas un champ, mais
 * il se reporte de la même façon (cahier L120-121).
 */
export type BedDeferralGroup = BedDeferrableGroup | 'certificat';

export const BED_DEFERRAL_GROUP_LABELS: Record<BedDeferralGroup, string> = {
  passport: 'Passeport',
  permis: 'Permis de conduire',
  ceam: 'Carte européenne d’assurance maladie ou équivalent',
  vehicule: 'Véhicule',
  certificat: 'Certificat médical',
};

export const BED_ROLE_LABELS: Record<string, string> = {
  pilot: 'Pilote',
  copilot: 'Copilote',
  common: 'Équipage',
};

/** Rôle normalisé d'un champ (`null` → `'common'`), pour indexer les maps. */
export function roleKeyOf(role: TargetRole | undefined): string {
  return role ?? 'common';
}

/** Clé de report d'un groupe pour un rôle donné. */
export function deferralKey(group: BedDeferralGroup, role: string): string {
  return `${group}:${role}`;
}

/** Report déclaré : la date à laquelle le participant s'engage à fournir. */
export interface BedDeferral {
  due_date: string;
}

export type BedDeferralMap = Record<string, BedDeferral>;

/**
 * Un certificat déposé remplace l'engagement à le fournir. La case est masquée
 * dès le dépôt : un report resté coché ferait compter le certificat comme
 * manquant (miroir client, `bed_compute_missing_items` 00252 puis snapshot
 * 00264), donc relancé alors qu'il est fourni. Seuls les reports
 * `certificat:<rôle>` des rôles couverts tombent. Renvoie la même référence
 * quand rien ne change.
 */
export function dropCoveredCertificateDeferrals(
  deferred: BedDeferralMap,
  certificates: Partial<Record<string, unknown>>
): BedDeferralMap {
  const covered = Object.keys(certificates)
    .filter((role) => certificates[role])
    .map((role) => deferralKey('certificat', role))
    .filter((key) => key in deferred);
  if (covered.length === 0) return deferred;
  const next = { ...deferred };
  for (const key of covered) delete next[key];
  return next;
}

// -----------------------------------------------------------------------
// VALEURS
// -----------------------------------------------------------------------

export type BedValues = Record<string, string>;

/** Valeurs stockées pour les cases à cocher (compatibles trigger SQL). */
export const BED_CHECKBOX_TRUE = 'true';
export const BED_CHECKBOX_FALSE = '';

/**
 * Valeurs du champ `is_not_student` — reprises TELLES QUELLES du socle : le
 * trigger `create_contact_from_submission` reconnaît 'Jeune Actif' comme
 * « n'est plus étudiant ». Ne pas les remplacer par des booléens.
 */
export const STUDENT_TRUE_VALUE = 'Jeune Actif';
export const STUDENT_FALSE_VALUE = 'Étudiant';

export function isChecked(values: BedValues, fieldId: string | undefined): boolean {
  if (!fieldId) return false;
  const raw = values[fieldId];
  return raw === BED_CHECKBOX_TRUE || raw === 'on' || raw === '1';
}

/**
 * « N'est plus étudiant(e) » — le champ est textuel ('Jeune Actif' /
 * 'Étudiant', contrat du trigger SQL). Tolère aussi les booléens sérialisés
 * qu'un pré-remplissage ancien pourrait avoir laissés.
 */
export function isNotStudent(values: BedValues, fieldId: string | undefined): boolean {
  if (!fieldId) return false;
  const raw = values[fieldId];
  return raw === STUDENT_TRUE_VALUE || raw === 'true' || raw === '1';
}

function isFilled(values: BedValues, fieldId: string): boolean {
  return String(values[fieldId] ?? '').trim() !== '';
}

/** Champ optionnel de l'index effectivement renseigné (absent = non rempli). */
function isFieldFilled(
  ctx: Pick<BedRuleContext, 'values'>,
  field: FormFieldWithValues | undefined
): boolean {
  return Boolean(field && isFilled(ctx.values, field.id));
}

// -----------------------------------------------------------------------
// INDEX DES CHAMPS PAR RÔLE
// -----------------------------------------------------------------------

/**
 * Repères dont les règles conditionnelles ont besoin. Les champs sans
 * `contact_field_map` sont identifiés par leur `order_index` : la convention
 * 00231 fixe les slots (x410 = « pas de CEAM », x500 = « déjà participé »,
 * x510 = édition, x7xx = adresse de réception du courrier).
 */
const SLOT_NO_CEAM = 410;
const SLOT_ALREADY_PARTICIPATED = 500;
const SLOT_PREVIOUS_EDITION = 510;
// 00252 (retour 6) : n° d'équipage de l'édition précédente, sous « déjà participé ».
const SLOT_PREVIOUS_CREW_NUMBER = 520;
// 00252 (retour 18) : propriétaire tiers — order_index ABSOLUS (étape 4, rôle commun).
const OI_NOT_OWNER = 4110;
const OI_OWNER_FIELDS = [4120, 4130];
const SLOT_MAIL_ADDRESS_MIN = 700;
const SLOT_MAIL_ADDRESS_MAX = 799;

function slotOf(field: FormFieldWithValues): number {
  return field.order_index % 1000;
}

/** Un champ appartient-il au bloc « adresse de réception du courrier » ? */
export function isMailAddressField(field: FormFieldWithValues): boolean {
  const slot = slotOf(field);
  return slot >= SLOT_MAIL_ADDRESS_MIN && slot <= SLOT_MAIL_ADDRESS_MAX;
}

export interface BedFieldIndex {
  /** `is_not_student` par rôle. */
  student: Record<string, FormFieldWithValues>;
  school: Record<string, FormFieldWithValues>;
  schoolCity: Record<string, FormFieldWithValues>;
  profession: Record<string, FormFieldWithValues>;
  noCeam: Record<string, FormFieldWithValues>;
  ceamNumber: Record<string, FormFieldWithValues>;
  alreadyParticipated: Record<string, FormFieldWithValues>;
  previousEdition: Record<string, FormFieldWithValues>;
  previousCrewNumber: Record<string, FormFieldWithValues>;
  mailAddress: Record<string, FormFieldWithValues[]>;
  /** 00252 : case « Je ne suis pas le propriétaire du véhicule » (étape 4). */
  notOwner: FormFieldWithValues | null;
  /** 00252 : nom + prénom du propriétaire, visibles/obligatoires si la case est cochée. */
  ownerFields: FormFieldWithValues[];
}

export function buildFieldIndex(fields: FormFieldWithValues[]): BedFieldIndex {
  const index: BedFieldIndex = {
    student: {},
    school: {},
    schoolCity: {},
    profession: {},
    noCeam: {},
    ceamNumber: {},
    alreadyParticipated: {},
    previousEdition: {},
    previousCrewNumber: {},
    mailAddress: {},
    notOwner: null,
    ownerFields: [],
  };

  for (const field of fields) {
    const role = roleKeyOf(field.target_role);
    const slot = slotOf(field);

    switch (field.contact_field_map) {
      case 'is_not_student':
        index.student[role] = field;
        continue;
      case 'school':
        index.school[role] = field;
        continue;
      case 'school_city':
        index.schoolCity[role] = field;
        continue;
      case 'socio_professional_category':
        index.profession[role] = field;
        continue;
      default:
        break;
    }

    if (field.bed_deferrable_group === 'ceam') {
      index.ceamNumber[role] = field;
    }
    if (slot === SLOT_NO_CEAM) index.noCeam[role] = field;
    if (slot === SLOT_ALREADY_PARTICIPATED) index.alreadyParticipated[role] = field;
    if (slot === SLOT_PREVIOUS_EDITION) index.previousEdition[role] = field;
    if (slot === SLOT_PREVIOUS_CREW_NUMBER) index.previousCrewNumber[role] = field;
    if (field.order_index === OI_NOT_OWNER) index.notOwner = field;
    if (OI_OWNER_FIELDS.includes(field.order_index)) index.ownerFields.push(field);
    if (isMailAddressField(field)) {
      (index.mailAddress[role] ??= []).push(field);
    }
  }

  return index;
}

// -----------------------------------------------------------------------
// RÔLES ACTIFS
// -----------------------------------------------------------------------

/**
 * Un rôle est « actif » dès qu'au moins un de ses champs porte une valeur.
 * Miroir EXACT de la règle serveur (`bed_compute_missing_items`) : un équipage
 * sans copilote ne se voit pas reprocher trente champs fantômes.
 */
export function activeRoles(fields: FormFieldWithValues[], values: BedValues): Set<string> {
  const active = new Set<string>(['common']);
  for (const field of fields) {
    if (isFilled(values, field.id)) active.add(roleKeyOf(field.target_role));
  }
  return active;
}

// -----------------------------------------------------------------------
// VISIBILITÉ ET EXIGENCE CONDITIONNELLES
// -----------------------------------------------------------------------

export interface BedRuleContext {
  index: BedFieldIndex;
  values: BedValues;
  deferred: BedDeferralMap;
  active: Set<string>;
}

export function buildRuleContext(
  fields: FormFieldWithValues[],
  values: BedValues,
  deferred: BedDeferralMap
): BedRuleContext {
  return {
    index: buildFieldIndex(fields),
    values,
    deferred,
    active: activeRoles(fields, values),
  };
}

/** Le participant a-t-il reporté ce groupe pour ce rôle ? */
export function isGroupDeferred(
  ctx: Pick<BedRuleContext, 'deferred'>,
  group: BedDeferralGroup,
  role: string
): boolean {
  return Boolean(ctx.deferred[deferralKey(group, role)]);
}

/**
 * Revue du 22/09 (retour 3) : valeurs à FORCER à vide dans l'envoi d'un rôle,
 * parce que leur champ est masqué à l'écran. Aujourd'hui une seule règle : la
 * profession, masquée dès qu'une école est renseignée. Sans cela une profession
 * pré-remplie depuis le CRM partait au serveur, qui en déduisait « plus
 * étudiant » (00052) et ignorait l'école que le participant voyait.
 */
export function hiddenValuesToClear(ctx: BedRuleContext, role: string): BedValues {
  const out: BedValues = {};
  const profession = ctx.index.profession[role];
  if (profession && !isFieldVisible(profession, ctx)) out[profession.id] = '';
  return out;
}

/**
 * Recette 4L du 22/09 (retour 37) : nom et prénom du participant, tels qu'il
 * vient de les saisir dans le bulletin — pour les porter sous sa signature.
 * Chaîne vide tant que rien n'est saisi : on n'écrit jamais un nom qu'on n'a
 * pas. Lit les champs mappés `first_name` / `last_name` du rôle demandé.
 */
export function participantFullName(
  fields: FormFieldWithValues[],
  values: BedValues,
  role: string
): string {
  const read = (map: 'first_name' | 'last_name') => {
    const field = fields.find(
      (f) => f.contact_field_map === map && roleKeyOf(f.target_role) === role
    );
    return field ? String(values[field.id] ?? '').trim() : '';
  };
  return [read('first_name'), read('last_name')].filter(Boolean).join(' ');
}

/**
 * Visibilité d'un champ. Règles du cahier :
 *  - « je ne suis plus étudiant(e) » coché → école ET ville d'école masquées ;
 *  - « mon pays ne fournit pas de CEAM » coché → numéro CEAM masqué ;
 *  - « a déjà participé » décoché → champ « quelle édition » masqué.
 */
export function isFieldVisible(field: FormFieldWithValues, ctx: BedRuleContext): boolean {
  const role = roleKeyOf(field.target_role);
  const notStudent = isNotStudent(ctx.values, ctx.index.student[role]?.id);

  if (
    (field.contact_field_map === 'school' || field.contact_field_map === 'school_city') &&
    notStudent
  ) {
    return false;
  }

  // Recette 4L du 22/09 (retour 3) : l'exclusion école / profession joue dans
  // les DEUX sens. Le sens « plus étudiant → école masquée » existait ; l'autre
  // manquait, alors que le serveur tranche déjà ainsi (00052 : une profession
  // renseignée vaut `is_not_student` et l'école saisie est ignorée).
  if (
    ctx.index.profession[role]?.id === field.id &&
    !notStudent &&
    isFieldFilled(ctx, ctx.index.school[role])
  ) {
    return false;
  }

  if (field.bed_deferrable_group === 'ceam' && isChecked(ctx.values, ctx.index.noCeam[role]?.id)) {
    return false;
  }

  if (
    ctx.index.previousEdition[role]?.id === field.id &&
    !isChecked(ctx.values, ctx.index.alreadyParticipated[role]?.id)
  ) {
    return false;
  }

  // 00252 (retour 6) : le n° d'équipage précédent suit la même règle que l'édition.
  if (
    ctx.index.previousCrewNumber[role]?.id === field.id &&
    !isChecked(ctx.values, ctx.index.alreadyParticipated[role]?.id)
  ) {
    return false;
  }

  // 00252 (retour 18) : nom/prénom du propriétaire seulement si « pas propriétaire ».
  if (
    ctx.index.ownerFields.some((f) => f.id === field.id) &&
    !isChecked(ctx.values, ctx.index.notOwner?.id)
  ) {
    return false;
  }

  return true;
}

/**
 * Exigence effective d'un champ, une fois les règles conditionnelles appliquées.
 * Un champ invisible n'est jamais requis.
 */
export function isFieldRequired(field: FormFieldWithValues, ctx: BedRuleContext): boolean {
  if (!isFieldVisible(field, ctx)) return false;

  const role = roleKeyOf(field.target_role);

  // Rôle inactif (copilote absent) : rien n'est exigé tant que rien n'est saisi.
  if (role !== 'common' && !ctx.active.has(role)) return false;

  // Groupe reporté : le participant s'est engagé sur une date, on n'exige plus.
  if (field.bed_deferrable_group && isGroupDeferred(ctx, field.bed_deferrable_group, role)) {
    return false;
  }

  // Cahier L47 : « plus étudiant » coché → la profession devient obligatoire.
  if (ctx.index.profession[role]?.id === field.id) {
    return isNotStudent(ctx.values, ctx.index.student[role]?.id);
  }

  // Cahier L46 : la ville n'est exigée que si une école est saisie (elle sert à
  // la création de l'école, qui doit porter nom + ville).
  if (field.contact_field_map === 'school_city') {
    const schoolField = ctx.index.school[role];
    return Boolean(schoolField && isFilled(ctx.values, schoolField.id));
  }

  // « Si oui, quelle édition » : requis dès que « a déjà participé » est coché.
  if (ctx.index.previousEdition[role]?.id === field.id) return true;

  // 00252 (retour 18) : visibles ⇒ obligatoires (la case « pas propriétaire » est cochée).
  if (ctx.index.ownerFields.some((f) => f.id === field.id)) return true;

  // Adresse de réception du courrier : bloc « tout ou rien ». Facultatif tant
  // qu'il est vide, entièrement requis dès qu'une ligne est renseignée.
  if (isMailAddressField(field)) {
    const block = ctx.index.mailAddress[role] ?? [];
    return block.some((f) => isFilled(ctx.values, f.id));
  }

  return field.is_required;
}

// -----------------------------------------------------------------------
// ÉLÉMENTS MANQUANTS
// -----------------------------------------------------------------------

export type BedMissingKind = 'deferred' | 'empty';

export interface BedMissingItem {
  /** `field:<uuid>` ou `<groupe>:<rôle>`. */
  code: string;
  kind: BedMissingKind;
  label: string;
  role: string;
  role_label: string;
  group?: string | null;
  due_date?: string | null;
  field_id?: string | null;
}

/**
 * Miroir client de `bed_compute_missing_items` (00231). Mêmes règles, même
 * ordre (reports d'abord, puis les champs vides dans l'ordre du formulaire).
 * Le serveur reste la référence pour ce qui est PERSISTÉ ; cette fonction
 * alimente l'affichage immédiat (frise, en-tête) entre deux enregistrements.
 */
export function computeMissingItems(
  fields: FormFieldWithValues[],
  values: BedValues,
  deferred: BedDeferralMap
): BedMissingItem[] {
  const ctx = buildRuleContext(fields, values, deferred);

  const deferredItems: BedMissingItem[] = Object.keys(deferred)
    .sort()
    .map((code) => {
      const [group, role] = code.split(':');
      return {
        code,
        kind: 'deferred' as const,
        group,
        role,
        label: BED_DEFERRAL_GROUP_LABELS[group as BedDeferralGroup] ?? group,
        role_label: BED_ROLE_LABELS[role] ?? role,
        due_date: deferred[code]?.due_date ?? null,
      };
    });

  const emptyItems: BedMissingItem[] = [...fields]
    .sort((a, b) => a.order_index - b.order_index)
    .filter((field) => isFieldRequired(field, ctx) && !isFilled(values, field.id))
    .map((field) => {
      const role = roleKeyOf(field.target_role);
      return {
        code: `field:${field.id}`,
        kind: 'empty' as const,
        group: field.bed_deferrable_group ?? null,
        role,
        label: field.label,
        role_label: BED_ROLE_LABELS[role] ?? role,
        field_id: field.id,
      };
    });

  return [...deferredItems, ...emptyItems];
}

// -----------------------------------------------------------------------
// ÉTAT DE LA FRISE
// -----------------------------------------------------------------------

/**
 * - `complete` : rien ne manque sur l'étape ;
 * - `warning`  : navigable (rien de bloquant) mais au moins un « je fournirai
 *                plus tard » — c'est le ⚠ de la frise ;
 * - `partial`  : commencée, il reste des champs requis vides ;
 * - `todo`     : pas encore touchée.
 */
export type BedStepState = 'complete' | 'warning' | 'partial' | 'todo';

export interface BedStepExtras {
  /** Étape ⑤ : une case « j'ai pris connaissance » manque-t-elle ? */
  optionsBlocked?: boolean;
  /** Étape ⑥ : signatures manquantes. */
  signaturesMissing?: boolean;
  /** Reports déclarés hors champs de formulaire (certificat médical). */
  extraDeferrals?: string[];
  /** 00252 (retour 10) : champs remplis mais INVALIDES (passeport expirant trop tôt). */
  invalidFieldIds?: string[];
}

export function computeStepState(
  step: BedStepId,
  fieldsOfStep: FormFieldWithValues[],
  ctx: BedRuleContext,
  extras: BedStepExtras = {}
): BedStepState {
  const visible = fieldsOfStep.filter((f) => isFieldVisible(f, ctx));
  const required = visible.filter((f) => isFieldRequired(f, ctx));
  const missing = required.filter((f) => !isFilled(ctx.values, f.id));
  const touched = visible.some((f) => isFilled(ctx.values, f.id));

  // Reports rattachés à cette étape (groupes des champs qu'elle contient, plus
  // les reports hors formulaire déclarés par l'appelant — cf. certificat ⑥).
  const stepGroups = new Set<string>();
  for (const field of fieldsOfStep) {
    if (field.bed_deferrable_group) {
      stepGroups.add(deferralKey(field.bed_deferrable_group, roleKeyOf(field.target_role)));
    }
  }
  (extras.extraDeferrals ?? []).forEach((key) => stepGroups.add(key));
  const hasDeferral = [...stepGroups].some((key) => Boolean(ctx.deferred[key]));

  const blocked =
    Boolean(extras.optionsBlocked) ||
    Boolean(extras.signaturesMissing) ||
    (extras.invalidFieldIds ?? []).length > 0;

  if (missing.length > 0 || blocked) {
    return touched || hasDeferral || blocked ? 'partial' : 'todo';
  }
  if (hasDeferral) return 'warning';
  if (!touched && visible.length > 0 && step !== 6) return 'todo';
  return 'complete';
}

/** Une étape est franchissable dès qu'elle n'a plus rien de bloquant. */
export function isStepSatisfied(state: BedStepState): boolean {
  return state === 'complete' || state === 'warning';
}

/**
 * « Continuer » est-il permis depuis cette étape ? (BUG-WIZ-02) Oui si elle est
 * satisfaite, et aussi pour l'étape copilote VIERGE d'un équipage sans
 * copilote : rien n'y est exigé (`isFieldRequired`, rôle inactif). Elle reste
 * « à remplir » sur la frise et sert toujours d'atterrissage (le participant
 * peut y déclarer un copilote), mais ne bloque plus — sans report fantôme à
 * cocher. Une étape vierge commune ou pilote reste bloquante.
 */
export function canLeaveStep(
  state: BedStepState,
  fieldsOfStep: FormFieldWithValues[],
  ctx: BedRuleContext
): boolean {
  if (isStepSatisfied(state)) return true;
  return (
    state === 'todo' &&
    fieldsOfStep.length > 0 &&
    fieldsOfStep.every((f) => f.target_role === 'copilot') &&
    !ctx.active.has('copilot')
  );
}

/**
 * Étape sur laquelle atterrir à la réouverture du lien (cahier L94) : la
 * première non satisfaite ; si tout est satisfait, le récapitulatif ⑥.
 */
export function landingStep(states: Record<BedStepId, BedStepState>): BedStepId {
  for (const step of BED_STEPS) {
    if (!isStepSatisfied(states[step.id])) return step.id;
  }
  return BED_LAST_STEP;
}

// -----------------------------------------------------------------------
// MESSAGE BLOQUANT DE L'ÉTAPE COURANTE (revue adversariale, 09/2026)
// -----------------------------------------------------------------------

export interface BedBlockerInput {
  step: BedStepId;
  /** `isStepSatisfied(stepStates[step])` — l'étape affichée est-elle franchie ? */
  stepSatisfied: boolean;
  /** Une prise de connaissance obligatoire manque (étape ⑤). */
  optionsBlocked: boolean;
  /** La question « chambre double » est sans réponse (étape ⑤). */
  doubleRoomMissing?: boolean;
  /** Au moins une signature manque (étape ⑥). */
  signaturesMissing: boolean;
  /** Un participant n'a ni certificat ni engagement à le fournir (étape ⑥). */
  certificatesMissing?: boolean;
}

/**
 * Ce qu'il faut dire au participant quand il vient de buter sur « Continuer »
 * ou « Envoyer ». `null` = rien à signaler.
 *
 * Le cas qui a motivé l'extraction : sur la DERNIÈRE étape, `handleSubmit`
 * refuse l'envoi si une prise de connaissance manque (étape ⑤) — une cause
 * INVISIBLE depuis le récapitulatif. L'écran ne montrait alors qu'un message
 * conditionné à l'étape courante non satisfaite… or l'étape ⑥ pouvait être
 * satisfaite : le bouton ne faisait rien, sans un mot. On nomme donc la cause
 * ET l'étape où la corriger.
 */
export function describeStepBlocker(input: BedBlockerInput): string | null {
  const isLast = input.step === BED_LAST_STEP;

  if (isLast) {
    const causes: string[] = [];
    if (input.optionsBlocked) {
      causes.push(
        'Retournez à l’étape 5 « Options » : une case « J’ai pris connaissance du document » n’est pas cochée.'
      );
    }
    if (input.doubleRoomMissing) {
      causes.push(
        'Retournez à l’étape 5 « Options » : répondez à la question sur la chambre double.'
      );
    }
    if (input.signaturesMissing) {
      causes.push('Chaque participant doit signer ci-dessus avant l’envoi.');
    }
    if (input.certificatesMissing) {
      causes.push(
        'Joignez le certificat médical de chaque participant, ou cochez « Je n’ai pas cette info à disposition ».'
      );
    }
    if (causes.length > 0) {
      return `Votre bulletin ne peut pas être envoyé. ${causes.join(' ')}`;
    }
  }

  if (input.stepSatisfied) return null;
  if (input.step === 5) {
    return input.doubleRoomMissing && !input.optionsBlocked
      ? 'Répondez à la question sur la chambre double avant de continuer.'
      : 'Prenez connaissance des documents obligatoires avant de continuer.';
  }
  if (isLast) return 'Chaque participant doit signer avant l’envoi du bulletin.';
  return 'Complétez les champs obligatoires, ou cochez « je m’engage à communiquer avant le … ».';
}

// -----------------------------------------------------------------------
// DOCUMENTS DE CONDITIONS (exigence assureur, cahier L91)
// -----------------------------------------------------------------------

/**
 * Une prise de connaissance ne vaut que pour la VERSION AFFICHÉE du document :
 * si l'assureur remplace le fichier, l'ancienne preuve ne compte plus. C'est la
 * règle exacte du socle 00166 (`conditions_url_snapshot = conditions_url`),
 * reproduite ici pour que l'UI verrouille avant même l'aller-retour serveur.
 * Un item sans document n'a rien à acquitter : il est acquitté par nature.
 */
export function isConditionsAcknowledged(
  item: { pricing_item_id: string; conditions_url: string | null },
  acknowledged: Record<string, string>
): boolean {
  if (!item.conditions_url) return true;
  return acknowledged[item.pricing_item_id] === item.conditions_url;
}

// -----------------------------------------------------------------------
// MONTANTS
// -----------------------------------------------------------------------

export interface BedAmountInput {
  /** Solde serveur (obligatoires − acompte − réduction), en centimes. */
  soldeCents: number;
  /** Somme des options facultatives cochées, en centimes. */
  optionsCents: number;
}

/** Total dû = solde serveur + options choisies. Jamais recalculé autrement. */
export function computeTotalCents({ soldeCents, optionsCents }: BedAmountInput): number {
  return soldeCents + optionsCents;
}

/**
 * Borne le montant que le participant choisit de régler maintenant : au moins
 * 1 centime, jamais plus que le restant dû (cahier : « il peut le diminuer
 * mais ne jamais mettre plus que le montant global restant à payer »).
 */
export function clampAmountToPay(requestedCents: number, totalCents: number): number {
  if (!Number.isFinite(requestedCents)) return totalCents;
  return Math.min(Math.max(Math.round(requestedCents), 1), totalCents);
}

/** Formatage monétaire FR à partir de centimes. */
export function formatCents(cents: number): string {
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(cents / 100);
}

// -----------------------------------------------------------------------
// TABLEAU DES MONTANTS — revue du 23/09 (points 1, 6, 7, 8)
// -----------------------------------------------------------------------

/**
 * Codes de grille qui désignent le poste d'engagement : `engagement` (seed,
 * saisie usuelle du staff) et `legacy_total` (backfill 00153 de l'ancien prix
 * global). Comparaison sans casse ni espaces : le code est saisi à la main.
 */
export const ENGAGEMENT_ITEM_CODES: readonly string[] = ['engagement', 'legacy_total'];

export function isEngagementItem(item: { code?: string | null }): boolean {
  const code = item.code?.trim().toLowerCase();
  return Boolean(code) && ENGAGEMENT_ITEM_CODES.includes(code as string);
}

/**
 * Recette du 22/09 (retour 35), revue du 23/09 (point 1). La maquette pose
 * « Montant de votre engagement » HORS souscriptions obligatoires, puis les
 * détaille. Le serveur, lui, met TOUS les postes obligatoires dans
 * `prix_total_cents` et les liste tous dans `mandatory_items` : sans critère,
 * « prix total − Σ obligatoires » vaut 0 par construction.
 *
 * Le critère est le code du poste (00273). Tant qu'aucun poste obligatoire ne
 * porte un code d'engagement, on ne devine pas : l'engagement vaut le prix
 * total et rien n'est décomposé — l'affichage d'avant la recette, jamais 0 €.
 * La somme affichée est toujours `prixTotalCents`, au centime.
 */
export function computeEngagementBreakdown<
  T extends { pricing_item_id: string; label: string; amount_cents: number; code?: string | null },
>(prixTotalCents: number, mandatoryItems: T[]): { engagementCents: number; subscriptions: T[] } {
  if (!mandatoryItems.some(isEngagementItem)) {
    return { engagementCents: prixTotalCents, subscriptions: [] };
  }
  const subscriptions = mandatoryItems.filter((item) => !isEngagementItem(item));
  const subscriptionsTotal = subscriptions.reduce((sum, item) => sum + item.amount_cents, 0);
  return { engagementCents: prixTotalCents - subscriptionsTotal, subscriptions };
}

/** Ce qui est déjà versé et ce qu'il reste à régler, sous le TOTAL. */
export interface BedSettlementLines {
  paidCents: number;
  remainingCents: number;
}

/**
 * Revue du 23/09 (points 7 et 8). Sous le TOTAL : « Déjà versé », puis « Reste
 * à régler » — TOTAL − déjà versé = reste, au lieu d'une ligne négative glissée
 * dans la décomposition que le TOTAL ne déduisait pas.
 *
 * Le reste vient du DOSSIER quand il existe (`resume_payment.remaining_cents`,
 * 00240) : c'est ce que le tunnel encaissera, même si le staff a changé une
 * remise ou un prix après un premier encaissement. Sans dossier, il se déduit
 * du total et des encaissements (00269). `null` = rien de versé, pas de ligne.
 */
export function computeSettlementLines(input: {
  totalCents: number;
  paiementsPartielsCents: number;
  resumeState?: {
    available: boolean;
    settled: boolean;
    paid_cents?: number;
    remaining_cents?: number;
  } | null;
}): BedSettlementLines | null {
  const partiels = Math.max(input.paiementsPartielsCents, 0);
  const state = input.resumeState;
  if (state?.settled) {
    return { paidCents: partiels, remainingCents: 0 };
  }
  if (state?.available && typeof state.remaining_cents === 'number') {
    const paid = typeof state.paid_cents === 'number' ? state.paid_cents : partiels;
    if (paid <= 0) return null;
    return { paidCents: paid, remainingCents: Math.max(state.remaining_cents, 0) };
  }
  if (partiels <= 0) return null;
  return { paidCents: partiels, remainingCents: Math.max(input.totalCents - partiels, 0) };
}

/** Montant NON figé : ce que le participant règle maintenant, et ce qui restera. */
export interface BedOpenAmountView {
  /** Restant dû auquel le montant se compare (total − déjà versé). */
  referenceCents: number;
  /** Ce que le tunnel encaissera pour ce choix. */
  chargeCents: number;
  /** Ce qui restera après ce paiement. */
  remainderCents: number;
}

/**
 * Revue du 23/09 (point 6). Le montant persisté (`amount_to_pay_cents`) est une
 * PROMESSE CUMULÉE : le tunnel encaisse `promesse − déjà encaissé`, et retombe
 * sur le restant dû entier si le résultat est ≤ 0 (00240 `get_bed_amount_due`,
 * même règle côté site). Premier envoi après un versement : le participant
 * raisonne sur ce qui lui reste — on lui montre le reste, et on convertit sa
 * saisie en promesse cumulée (`toCumulativePromise`).
 * Sans versement (`paidCents` = 0), la vue est celle d'avant : référence =
 * total, montant = choix borné au total.
 */
export function computeOpenAmountView(input: {
  totalCents: number;
  paidCents: number;
  promiseCents: number | null;
}): BedOpenAmountView {
  const paid = Math.max(input.paidCents, 0);
  const referenceCents = Math.max(input.totalCents - paid, 0);
  const promise = input.promiseCents ?? input.totalCents;
  const reliquat = promise - paid;
  const chargeCents = reliquat > 0 ? Math.min(reliquat, referenceCents) : referenceCents;
  return { referenceCents, chargeCents, remainderCents: referenceCents - chargeCents };
}

/** Saisie du participant (ce qu'il règle maintenant) → promesse cumulée persistée. */
export function toCumulativePromise(
  chargeCents: number,
  paidCents: number,
  referenceCents: number
): number {
  return clampAmountToPay(chargeCents, referenceCents) + Math.max(paidCents, 0);
}

/**
 * Promesse cumulée → ce que le tunnel encaissera (reliquat, 00240). `null` =
 * restant dû entier (aucun choix, ou choix déjà couvert par les versements).
 */
export function promiseRemainderCents(
  promiseCents: number | null,
  paidCents: number
): number | null {
  if (promiseCents === null) return null;
  if (paidCents <= 0) return promiseCents;
  const remainder = promiseCents - paidCents;
  return remainder > 0 ? remainder : null;
}

/**
 * Date limite au format FR pour les libellés « avant le … ». `null` si aucune
 * date (le libellé retombe sur une formulation sans date) ; la chaîne BRUTE si
 * elle ne se parse pas — on affiche ce que le serveur a fourni, jamais
 * « Invalid Date » (re-review 10/08).
 */
export function formatDueDateFr(due: string | null | undefined): string | null {
  if (!due) return null;
  const date = new Date(`${due}T00:00:00`);
  if (Number.isNaN(date.getTime())) return due;
  return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

// -----------------------------------------------------------------------
// ÉCRAN DE REPRISE DU PAIEMENT (migration 00240)
// -----------------------------------------------------------------------
// Le participant a pu choisir de ne régler qu'une PARTIE du solde. Le tunnel
// encaisse ce montant-là ; l'écran de reprise du CRM doit donc annoncer le
// même, pas le solde entier — sinon le CRM promet 3 920 € et le tunnel prélève
// 2 000 € (reliquat de la review site #174, incohérence symétrique de celle
// que Victor avait signalée côté site).
//
// Logique PURE, extraite du JSX pour être testable.

/** Sous-ensemble de l'état de reprise dont dépend l'affichage du CTA. */
export interface ResumeCtaAmounts {
  /** Montant choisi, déjà borné au restant dû côté serveur. `null` = aucun choix. */
  amount_to_pay_cents?: number | null;
  remaining_cents?: number;
}

/** Libellé du bouton : « Reprendre le paiement » + le montant réellement encaissé. */
export function buildResumeCtaLabel(state: ResumeCtaAmounts): string {
  const base = 'Reprendre le paiement';
  return typeof state.amount_to_pay_cents === 'number'
    ? `${base} — ${formatCents(state.amount_to_pay_cents)}`
    : base;
}

/**
 * Ligne secondaire. Quand le montant choisi diffère du restant dû (paiement
 * partiel), on montre AUSSI le solde : le participant doit savoir ce qu'il
 * restera après ce règlement.
 */
export function buildResumeCtaHint(state: ResumeCtaAmounts): string | null {
  if (typeof state.remaining_cents !== 'number') return null;

  const partial =
    typeof state.amount_to_pay_cents === 'number' &&
    state.amount_to_pay_cents !== state.remaining_cents;

  return partial
    ? `Montant choisi pour ce règlement. Reste à régler au total : ${formatCents(state.remaining_cents)}`
    : `Reste à régler : ${formatCents(state.remaining_cents)}`;
}

// -----------------------------------------------------------------------
// MONTANT FIGÉ PAR UN ENCAISSEMENT (00249, revue adversariale 09/2026)
// -----------------------------------------------------------------------

/** État de reprise dont dépend l'affichage d'un montant VERROUILLÉ. */
export interface LockedAmountState extends ResumeCtaAmounts {
  paid_cents?: number;
}

export interface BedLockedAmountView {
  /** Ce que le tunnel encaissera au prochain passage, en centimes. */
  chargeCents: number;
  /** Ce qui restera dû APRÈS ce paiement (0 = il soldera le dossier). */
  remainderCents: number;
  /** Déjà encaissé, `null` si le serveur ne l'a pas dit. */
  paidCents: number | null;
}

/**
 * Ce que le récapitulatif doit annoncer quand le montant est FIGÉ.
 *
 * Le champ affichait jusqu'ici la PROMESSE d'origine (« 2 000 € sur 3 920 € »)
 * alors que 00240 en soustrait le déjà-encaissé : promesse épuisée, le tunnel
 * prélevait en réalité le restant dû (1 920 €) et l'écran de fin juxtaposait
 * deux montants contradictoires. On repart donc de l'état de reprise, seul
 * endroit où ce calcul est déjà fait côté serveur :
 *   `amount_to_pay_cents` = RELIQUAT de la promesse, `null` = épuisée ou aucun
 *   choix → le tunnel encaisse `remaining_cents`.
 *
 * `null` quand le restant dû est inconnu : on préfère ne rien affirmer plutôt
 * qu'afficher un chiffre que le tunnel démentira.
 */
export function computeLockedAmountView(
  state: LockedAmountState | null | undefined
): BedLockedAmountView | null {
  if (!state || typeof state.remaining_cents !== 'number') return null;

  const remaining = Math.max(state.remaining_cents, 0);
  const chosen = state.amount_to_pay_cents;
  const chargeCents =
    typeof chosen === 'number' && chosen > 0 ? Math.min(chosen, remaining) : remaining;

  return {
    chargeCents,
    remainderCents: Math.max(remaining - chargeCents, 0),
    paidCents: typeof state.paid_cents === 'number' ? state.paid_cents : null,
  };
}

// -----------------------------------------------------------------------
// SOUMISSION — idempotence du retry (re-review 10/08, M4)
// -----------------------------------------------------------------------
// La soumission duo écrit un enregistrement PAR RÔLE. Si le copilote échoue
// après que le pilote est passé, le retry ne doit pas re-soumettre le pilote :
// on mémorise (id, payload) par rôle et on ne réutilise l'id que si le payload
// est STRICTEMENT identique — une saisie modifiée entre deux essais repart,
// elle, en soumission fraîche.

export interface RoleSubmissionCacheEntry {
  id: string;
  payload: string;
}

export type RoleSubmissionCache = Partial<Record<'pilot' | 'copilot', RoleSubmissionCacheEntry>>;

/** Id réutilisable pour ce rôle, ou `null` s'il faut (re)soumettre. */
export function reusableSubmissionId(
  cache: RoleSubmissionCache,
  role: 'pilot' | 'copilot',
  payload: string
): string | null {
  const entry = cache[role];
  return entry && entry.payload === payload ? entry.id : null;
}

export interface PostSubmitResumeInput {
  /** `finalize_bed_submission` a réussi (c'est elle qui pose `submitted_at`). */
  finalizeOk: boolean;
  /** Wizard monté en iframe (le parent vient de recevoir `payment_ready`). */
  isEmbedded: boolean;
  paymentCaseId: string;
  orderId?: string;
  /** `null` = aucun choix explicite → le tunnel encaissera le restant dû. */
  amountToPayCents: number | null;
}

// ---------------------------------------------------------------------------
// Réédition d'un bulletin soumis (00248, retour recette du 04/09/2026)
// ---------------------------------------------------------------------------

/**
 * Statuts CRM qui laissent le participant rouvrir et re-soumettre son bulletin :
 * le cycle BED AVANT complétion. Miroir de `bed_resubmit_allowed` côté serveur ;
 * la décision qui fait foi reste celle du bootstrap et de la RPC — ce miroir ne
 * sert qu'à rafraîchir l'écran juste après une (re)soumission, sans recharger.
 */
export const BED_RESUBMITTABLE_STATUSES: readonly string[] = [
  'bpi_payment_pending',
  'pre_registered',
  'bed_downloaded',
  'bed_incomplete',
  'bed_incomplete_payment',
];

export function isResubmittableStatus(status: string | null | undefined): boolean {
  return !!status && BED_RESUBMITTABLE_STATUSES.includes(status);
}

/**
 * Éléments manquants à AFFICHER pour un bulletin déjà soumis (00249).
 *
 * L'archive `bed_drafts.missing_items` est volontairement BRUTE : c'est la
 * matière que le CRM annote des levées manuelles du staff. Le serveur expose
 * donc à côté la liste EFFECTIVE (`effective_missing_items`) — la même dont il
 * dérive le statut CRM. Sans ce choix, le participant lisait « il reste 1
 * élément » pendant que sa fiche équipage était classée « BED incomplet —
 * paiement » : un passeport reçu par mail lui était re-réclamé.
 *
 * La liste effective fait foi dès qu'elle est présente, y compris VIDE (c'est
 * précisément le cas « le staff a tout levé »). Absente (bulletin non soumis,
 * ou serveur antérieur à 00249), on retombe sur l'archive.
 */
export function pickEffectiveMissingItems(
  archived: BedMissingItem[] | null | undefined,
  effective: BedMissingItem[] | null | undefined
): BedMissingItem[] {
  if (effective) return effective;
  return archived ?? [];
}

/** Forme minimale d'un élément manquant pour décider d'une levée. */
export interface ResolvableMissingItem {
  code: string;
  role: string;
  group?: string | null;
}

/**
 * Une levée manuelle du staff couvre-t-elle cet élément (00249) ?
 *
 * Miroir EXACT de la correspondance serveur (`bed_missing_items_snapshot`,
 * 00248 §5) : `item_code = code OR item_code = group || ':' || role`. Les deux
 * règles sont indispensables — le staff lève « passeport du pilote »
 * (`passport:pilot`, le code du REPORT) alors que l'élément recalculé côté
 * client peut porter un code de CHAMP (`field:<uuid>`, group `passport`, rôle
 * `pilot`). Ne filtrer que sur le code exact laisserait repasser la moitié des
 * cas.
 */
export function isMissingItemResolved(
  item: ResolvableMissingItem,
  resolvedItemCodes: readonly string[]
): boolean {
  if (resolvedItemCodes.length === 0) return false;
  if (resolvedItemCodes.includes(item.code)) return true;
  return Boolean(item.group) && resolvedItemCodes.includes(`${item.group}:${item.role}`);
}

/**
 * Retire d'une liste d'éléments manquants ceux que le staff a levés.
 *
 * À appliquer au miroir CLIENT (`computeMissingItems`), qui ne connaît pas les
 * levées, et à la liste brute que renvoie `save_bed_draft` : sans ça, l'écran
 * annonçait « Votre bulletin est complet » (liste effective du serveur) puis,
 * deux lignes plus bas, « 1 élément à fournir · Passeport — Pilote » (miroir
 * client). Une liste déjà effective traverse sans changement.
 */
export function excludeResolvedMissingItems<T extends ResolvableMissingItem>(
  items: readonly T[],
  resolvedItemCodes: readonly string[]
): T[] {
  if (resolvedItemCodes.length === 0) return [...items];
  return items.filter((item) => !isMissingItemResolved(item, resolvedItemCodes));
}

/**
 * Reports à conserver quand le participant rouvre un bulletin soumis. Demande
 * recette : les cases « je n'ai pas encore cette info » sont DÉCOCHÉES à
 * chaque passage, pour que les éléments manquants ressortent. Exception : les
 * éléments que le staff a déjà levés côté CRM (`resolved_item_codes`) restent
 * reportés — les redemander serait absurde, et décocher ferait réapparaître
 * comme manquant un document que l'administration a marqué reçu.
 */
export function deferralsToKeepOnResubmit(
  deferred: BedDeferralMap,
  resolvedItemCodes: readonly string[]
): BedDeferralMap {
  const keep = new Set(resolvedItemCodes);
  return Object.fromEntries(Object.entries(deferred).filter(([key]) => keep.has(key)));
}

/**
 * État de reprise à poser JUSTE APRÈS une soumission fraîche (re-review 10/08,
 * M2) — ou `null` quand le CTA ne doit PAS apparaître :
 *   - finalisation échouée → `submitted_at` n'est pas posé (00232:1316) et
 *     `bed_resume_payment` répondrait `bed_resume_unavailable` (00240:74) :
 *     le CTA serait un bouton garanti mort ;
 *   - en iframe → le parent vient de recevoir `payment_ready` et route vers le
 *     tunnel : un second CTA dans l'iframe ferait deux boutons concurrents.
 * Au rechargement, le bootstrap (décision SERVEUR) reprend la main dans tous
 * les cas.
 */
export function buildPostSubmitResumeState(input: PostSubmitResumeInput): {
  available: boolean;
  settled: boolean;
  payment_case_id: string;
  order_id?: string;
  amount_to_pay_cents: number | null;
} | null {
  if (!input.finalizeOk || input.isEmbedded) return null;
  const settled = input.amountToPayCents === 0;
  return {
    available: !settled,
    settled,
    payment_case_id: input.paymentCaseId,
    order_id: input.orderId,
    amount_to_pay_cents: input.amountToPayCents,
  };
}

// ---------------------------------------------------------------------------
// 00252 — retours de recette 4L (call du 08/09/2026)
// ---------------------------------------------------------------------------

/** Retour 10 : le champ « date d'expiration » d'un groupe passeport. */
export function isPassportExpiryField(
  field: Pick<FormFieldWithValues, 'bed_deferrable_group' | 'field_type' | 'label'>
): boolean {
  return (
    field.bed_deferrable_group === 'passport' &&
    field.field_type === 'date' &&
    /expir/i.test(field.label ?? '')
  );
}

/**
 * Retour 10 : message d'erreur si le passeport expire AVANT la date minimale
 * du produit (`bed_passport_min_expiry`, inclusive). `null` = valide (ou pas
 * de règle, ou champ vide — le vide relève de « requis », pas d'« invalide »).
 * Comparaison lexicographique ISO (AAAA-MM-JJ) : pas d'objet Date, donc jamais
 * d'« Invalid Date ».
 */
export function passportExpiryError(value: string, minExpiry: string | null): string | null {
  const v = (value ?? '').trim();
  const min = (minExpiry ?? '').trim();
  if (!v || !min || !/^\d{4}-\d{2}-\d{2}$/.test(v)) return null;
  if (v >= min) return null;
  return `Votre passeport expire avant la fin de l’événement : il doit être valide au moins jusqu’au ${formatDueDateFr(min)}. Renseignez un passeport valide, ou cochez « Je n’ai pas cette info à disposition ».`;
}

/** Retour 17 : immatriculation normalisée (majuscules, lettres et chiffres seulement). */
export function normalizeRegistration(raw: string): string {
  return (raw ?? '').toUpperCase().replace(/[^A-Z0-9]/g, '');
}
